import { CartDrawerCard as CartDrawerCardUI } from '@iheartjane/cart';
import {
  getCartItemWeight,
  hideStrikethroughPrice,
  productPhotoMetadata,
} from '@iheartjane/menu';
import { useDrawerContext } from '@iheartjane/reefer';
import type {
  PriceId,
  ProductCategory,
  ProductKind,
  ProductType,
} from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import { useNavigate } from 'react-router';

import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { clearTouchedProduct } from '@jane/monolith/customer/redux/bundlePossibilities';
import { useMoengage } from '@jane/shared-ecomm/tracking';
import { productDetailLink as generatePdpLink } from '@jane/shared/components';
import { useGetStore } from '@jane/shared/data-access';
import {
  useNeedsSignInForEligibility,
  useShouldShowGold,
} from '@jane/shared/hooks';
import { useHeadlessApp } from '@jane/shared/providers';
import type { WeightPriced } from '@jane/shared/util';
import {
  calculateDiscountPercentage,
  postMessageToIframeParent,
} from '@jane/shared/util';

/**
 * Structural so both the v1 Redux cart product and the v3 `CartProduct`
 * satisfy it.
 */
export type CartDrawerCardProduct = WeightPriced &
  Partial<Record<`max_cart_quantity_${PriceId}`, number | null>> & {
    amount?: string | null;
    brand?: string | null;
    brand_discount_label?: string | null;
    brand_discount_title?: string | null;
    brand_special_has_segment_rules?: boolean | null;
    brand_subtype?: string | null;
    category?: ProductCategory | null;
    count: number;
    id: number;
    image_urls?: readonly string[];
    kind?: ProductKind | null;
    max_cart_quantity?: number | null;
    name: string;
    price_id: PriceId;
    product_id?: number;
    root_subtype?: string | null;
    special_title?: string | null;
    type?: ProductType | null;
    url_slug?: string | null;
  };

interface CartDrawerCardProps {
  bloomUserExperience?: boolean;
  headless?: boolean;
  product: CartDrawerCardProduct;
  productDetailLink: string;
  productFullPrice: number;
  productPrice: number | null;
  storeId?: string | number;
}

export const CartDrawerCard = ({
  bloomUserExperience,
  headless,
  product,
  productDetailLink,
  productPrice,
  productFullPrice,
  storeId,
}: CartDrawerCardProps) => {
  const navigate = useNavigate();
  const dispatch = useCustomerDispatch();
  const { updateProduct, removeProduct: removeCartProduct } = useCartActions();
  const { data: store } = useGetStore(storeId);
  const { moeTrack } = useMoengage();
  const { options } = useHeadlessApp();

  const { onRequestClose } = useDrawerContext();

  const {
    amount,
    brand,
    brand_discount_label,
    brand_discount_title,
    brand_subtype,
    category,
    count,
    id,
    max_cart_quantity,
    name,
    price_id,
    special_title,
  } = product;
  const brandDiscountText = brand_discount_title || brand_discount_label;

  const shouldShowGold = useShouldShowGold({ store }) && !!brandDiscountText;

  const needsSignInForEligibility = useNeedsSignInForEligibility({
    cartProduct: product,
  });

  const perWeightMaxCartQuantity = product[`max_cart_quantity_${price_id}`];
  // v1 always sends the singular cap, so the `?? 0` is unreachable while Redux
  // backs the cart.
  const maxCartQuantity = perWeightMaxCartQuantity
    ? Number(perWeightMaxCartQuantity)
    : (max_cart_quantity ?? 0);

  const removeProduct = () => {
    removeCartProduct(product.id, product.price_id);
  };

  const editProductQuantity = (newCount: number) => {
    dispatch(clearTouchedProduct());
    if (count !== newCount) {
      if (headless) {
        const basePrice = Number(product[`price_${price_id}`]);
        const discountedPrice = Number(product[`discounted_price_${price_id}`]);
        postMessageToIframeParent({
          messageType: 'analyticsEvent',
          payload: {
            name: 'cartItemChangeCount',
            properties: {
              productId: id,
              priceId: price_id,
              count: newCount,
              previousCount: count,
              ...(basePrice > 0 && { price: basePrice }),
              ...(discountedPrice > 0 && { discountedPrice }),
            },
          },
        });
      }
      updateProduct(
        {
          product_id: id,
          count: newCount,
          price_id,
        },
        moeTrack
      );
    }
  };

  const { url: photoUrl } = productPhotoMetadata(product);

  const showWeight = price_id !== 'each' && !amount;
  const weightString = showWeight ? `/${getCartItemWeight(product)}` : '';

  const goToProductDetailPage = () => {
    if (headless) {
      postMessageToIframeParent({
        messageType: 'analyticsEvent',
        payload: {
          name: 'cartItemClickedInCheckout',
          properties: {
            productId: product.id,
            priceId: product.price_id,
            productCount: product.count,
            price: productPrice!,
          },
        },
      });

      postMessageToIframeParent({
        messageType: 'navigationEvent',
        payload: {
          name: 'navigateToProduct',
          link: generatePdpLink({
            product,
            appMode: bloomUserExperience ? 'bloom' : 'headless',
          }),
        },
      });
    }

    onRequestClose();
    navigate(productDetailLink);
  };

  const isDiscounted = productFullPrice !== productPrice;
  const fullPriceIsDiscounted =
    formatCurrency(productFullPrice * count) !==
    formatCurrency(productPrice! * count);

  const discountPercentage = calculateDiscountPercentage(
    productFullPrice,
    productPrice!
  );

  const specialBadgeColor = (
    options?.theme as { specialBadgeColor?: string } | undefined
  )?.specialBadgeColor;

  return (
    <CartDrawerCardUI
      bloomUserExperience={bloomUserExperience}
      brand={brand}
      brandDiscountText={brandDiscountText}
      brandSubtype={brand_subtype}
      category={category}
      count={count}
      discountPercentage={discountPercentage!}
      fullPriceIsDiscounted={fullPriceIsDiscounted}
      hideStrikethrough={hideStrikethroughPrice(store?.state || '')}
      isDiscounted={isDiscounted}
      maxCartQuantity={maxCartQuantity}
      name={name}
      needsSignInForEligibility={needsSignInForEligibility}
      onEditQuantity={editProductQuantity}
      onGoToProductDetailPage={goToProductDetailPage}
      onRemoveProduct={removeProduct}
      photoUrl={photoUrl}
      productFullPrice={productFullPrice}
      productPrice={productPrice}
      shouldShowGold={shouldShowGold && !!brandDiscountText}
      specialBadgeColor={specialBadgeColor}
      specialTitle={special_title}
      weightString={weightString}
    />
  );
};
